import React from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import isEmpty from "lodash/isEmpty";
import { Loading } from "@/components/loading/loading";
import { LoanOriginationWizard, OrganizationNotFound, useGetOrganizationDetailsQuery } from "@/modules/client/loan-application";
import withBorrowerLoanOriginationLayout from "@/layouts/clients/hoc/with-loan-origination-layout";
import SeoHead from "@/modules/client/loan-application/features/loan-origination/components/seo-head";
import { HEADERS } from "@/utils/applicationConstants";

/** PageContent — lives inside the layout HOC (may be blocked by layout loading). */
function PageContent({ organization }) {
  const router = useRouter();
  const { id } = router.query;
  const { data, isLoading } = useGetOrganizationDetailsQuery(id, { skip: !id });

  const hasServerData = !isEmpty(organization);
  const showLoading = !hasServerData && (!id || isLoading);
  const showNotFound = !hasServerData && !isLoading && isEmpty(data);

  if (showLoading) return <Loading />;
  if (showNotFound) return <OrganizationNotFound />;

  return <LoanOriginationWizard organizationId={id} organization={organization} />;
}

PageContent.propTypes = {
  organization: PropTypes.shape({ organizationName: PropTypes.string, logoUrl: PropTypes.string }),
};

const LayoutWrappedContent = withBorrowerLoanOriginationLayout(PageContent);

/** Renders SeoHead outside the layout HOC so meta tags always appear in server HTML. */
function BorrowerLoanOriginationPage({ organization, canonicalUrl }) {
  return (
    <>
      <SeoHead organization={organization} canonicalUrl={canonicalUrl} />
      <LayoutWrappedContent organization={organization} />
    </>
  );
}

export async function getServerSideProps(context) {
  context.res.setHeader("Cache-Control", "no-cache, no-store, must-revalidate");
  context.res.setHeader("Pragma", "no-cache");

  const { id } = context.params;
  const protocol = context.req.headers["x-forwarded-proto"] || "https";
  const host = context.req.headers.host || process.env.NEXT_PUBLIC_SITE_URL || "";
  const canonicalUrl = host ? `${protocol}://${host}${context.resolvedUrl}` : "";

  try {
    const baseUrl = (process.env.API_URL || "").replace(/\/+$/, "");
    const url = `${baseUrl}/organizations/${id}/loan-origination`;

    const res = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        [HEADERS.APP_ID]: process.env.NEXT_PUBLIC_APP_ID ?? "",
        [HEADERS.VERSION_ID]: process.env.NEXT_PUBLIC_APP_VERSION ?? "",
      },
    });

    if (!res.ok) return { props: { organization: null, canonicalUrl } };

    const organization = await res.json();
    return { props: { organization, canonicalUrl } };
  } catch {
    return { props: { organization: null, canonicalUrl } };
  }
}

BorrowerLoanOriginationPage.propTypes = {
  organization: PropTypes.shape({ organizationName: PropTypes.string, logoUrl: PropTypes.string }),
  canonicalUrl: PropTypes.string,
};

export default BorrowerLoanOriginationPage;
